import React, { useEffect, useState } from 'react'
import { useIprProgress } from '../hooks/useIprProgress'
import './IPRChecklist.css'

// Компонент для отображения чек-листа ИПР с возможностью отмечать выполненные задачи
const IPRChecklist = ({ userId, talentId, talentName, onProgressUpdate, compact = false }) => {
  const [iprContent, setIprContent] = useState('')
  const [checklist, setChecklist] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState(0)

  const iprHook = useIprProgress()

  // Загрузка ИПР и чек-листа
  useEffect(() => {
    const fetchIPR = async () => {
      try {
        setLoading(true)

        // Replace API call with hook invocation
        const result = await iprHook.getOrCreateIPR(userId, talentId, undefined)
        if (!result.success) throw new Error('Failed to get or create IPR from hook')
        const data = { ipr: result.ipr }

        setIprContent(data.ipr)

        let tasks
        if (typeof data.ipr === 'string') {
          tasks = parseIPRToChecklist(data.ipr)
        } else if (Array.isArray(data.ipr)) {
          tasks = data.ipr
        } else {
          throw new Error('Invalid IPR data type received')
        }

        // Загружаем статус выполнения задач сначала из базы данных, потом из локального хранилища
        const savedStatus = await loadChecklistStatus(userId, talentId)

        // Обновляем чек-лист с учетом сохраненного статуса
        const updatedChecklist = tasks.map((task) => {
          const savedTask = savedStatus.find((item) => item.taskId === task.taskId)
          return savedTask ? { ...task, completed: savedTask.completed } : task
        })

        setChecklist(updatedChecklist)
        updateProgress(updatedChecklist)
      } catch (err) {
        console.error('Ошибка при загрузке ИПР:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (userId && talentId) {
      fetchIPR()
    }
  }, [userId, talentId])

  // Улучшенная функция для парсинга текста ИПР и создания чек-листа
  const parseIPRToChecklist = (iprText) => {
    if (typeof iprText !== 'string') {
      console.error('Invalid iprText type in parseIPRToChecklist:', iprText)
      return []
    }
    const tasks = []
    const sections = [
      'Самообразование',
      'Развитие на рабочем месте',
      'Специальные проекты',
      'Новый опыт вне работы',
    ]

    let currentSection = ''
    let taskId = 1

    // Разбиваем текст на строки
    const lines = iprText.split('\n')

    console.log('Парсинг ИПР для чек-листа:', iprText.substring(0, 100) + '...')

    // Алгоритм 1: Парсинг списков с дефисами (оптимизированный формат, созданный по новой инструкции)
    lines.forEach((line, index) => {
      // Определяем текущий раздел
      for (const section of sections) {
        // Проверяем как на точное совпадение, так и на включение с цифрами (например "1. Самообразование")
        if (line.includes(section) || line.match(new RegExp(`\\d+\\.\\s+${section}`, 'i'))) {
          currentSection = section
          console.log(`Найден раздел "${section}" в строке ${index}: ${line}`)
          break
        }
      }

      // Обрабатываем элементы списка (начинаются с '- ' или с '• ')
      if ((line.trim().startsWith('- ') || line.trim().startsWith('• ')) && currentSection) {
        // Убираем маркеры списка
        let taskText = line.trim()
        if (taskText.startsWith('- ')) {
          taskText = taskText.substring(2)
        } else if (taskText.startsWith('• ')) {
          taskText = taskText.substring(2)
        }

        // Только если текст задачи не пустой
        if (taskText.length > 0) {
          // Дополнительная обработка для форматирования текста
          // Для книг: "Автор. Название книги." -> "**Автор. Название книги.**"
          if (
            currentSection === 'Самообразование' &&
            taskText.match(/\./) &&
            !taskText.includes('**')
          ) {
            // Находим конец предложения (обычно название книги заканчивается точкой)
            const endOfTitle = taskText.indexOf('.')
            if (endOfTitle > 0) {
              // Выделяем название книги и автора жирным
              const bookTitle = taskText.substring(0, endOfTitle + 1)
              const restText = taskText.substring(endOfTitle + 1)

              // Создаем отформатированный текст
              taskText = `**${bookTitle}**${restText}`
            }
          }

          tasks.push({
            taskId: `${talentId}-${taskId}`,
            section: currentSection,
            text: taskText,
            completed: false,
          })

          taskId++
        }
      }
    })

    console.log(`Найдено ${tasks.length} задач в ИПР для таланта ${talentId}`)

    // Если задач не найдено или их мало, возможно формат ИПР другой - используем резервный метод
    if (tasks.length < 4) {
      console.log('Найдено мало задач, используем дополнительный метод парсинга')

      // Алгоритм 2: Проверяем нумерованные списки
      currentSection = ''
      lines.forEach((line, index) => {
        // Проверяем, является ли строка заголовком раздела
        for (const section of sections) {
          if (line.includes(section)) {
            currentSection = section
            break
          }
        }

        // Проверяем нумерованные списки
        if (line.trim().match(/^\d+\.\s+/) && currentSection) {
          const taskText = line.trim().replace(/^\d+\.\s+/, '')
          if (taskText.length > 0 && !sections.some((s) => taskText.includes(s))) {
            // Проверяем, нет ли уже такой задачи
            if (!tasks.some((t) => t.text === taskText)) {
              tasks.push({
                taskId: `${talentId}-${taskId}`,
                section: currentSection,
                text: taskText,
                completed: false,
              })
              taskId++
            }
          }
        }
      })

      // Алгоритм 3: Более агрессивный парсинг по абзацам
      if (tasks.length < 8) {
        console.log('Всё еще мало задач, используем резервный метод парсинга по абзацам')

        // Разделяем текст на параграфы по двойным переносам строк
        const paragraphs = iprText.split('\n\n')

        // Ищем разделы
        paragraphs.forEach((paragraph, index) => {
          for (const section of sections) {
            if (paragraph.includes(section)) {
              currentSection = section

              // Проверяем следующий параграф на наличие задач
              if (index + 1 < paragraphs.length) {
                const nextParagraph = paragraphs[index + 1]

                // Разбиваем на отдельные задачи (предполагаем, что они разделены переносом строки)
                const potentialTasks = nextParagraph.split('\n')

                potentialTasks.forEach((task) => {
                  const taskText = task.trim()
                  // Убираем маркеры списка, если есть
                  let cleanTaskText = taskText
                  if (cleanTaskText.startsWith('- ') || cleanTaskText.startsWith('• ')) {
                    cleanTaskText = cleanTaskText.substring(2)
                  } else if (cleanTaskText.match(/^\d+\.\s+/)) {
                    cleanTaskText = cleanTaskText.replace(/^\d+\.\s+/, '')
                  }

                  if (
                    cleanTaskText.length > 0 &&
                    !sections.some((s) => cleanTaskText.includes(s))
                  ) {
                    // Проверяем, нет ли уже такой задачи
                    if (!tasks.some((t) => t.text === cleanTaskText)) {
                      tasks.push({
                        taskId: `${talentId}-${taskId}`,
                        section: currentSection,
                        text: cleanTaskText,
                        completed: false,
                      })
                      taskId++
                    }
                  }
                })
              }
            }
          }
        })
      }

      console.log(`После дополнительных методов парсинга найдено ${tasks.length} задач`)
    }

    // Валидация результатов: должны быть задачи в каждом разделе
    const sectionCounts = {}
    sections.forEach((section) => {
      sectionCounts[section] = tasks.filter((t) => t.section === section).length
    })

    console.log('Распределение задач по разделам:', sectionCounts)

    // Если не удалось разобрать задачи для всех секций, создаем заглушки
    let missingTasksCreated = false
    sections.forEach((section) => {
      if (sectionCounts[section] === 0) {
        missingTasksCreated = true
        console.log(`Не удалось найти задачи для раздела "${section}", создаем заглушки`)

        const placeholderTasks = [
          `Определите конкретные действия для раздела "${section}"`,
          `Составьте план действий для "${section}"`,
          `Запланируйте активности для "${section}"`,
        ]

        placeholderTasks.forEach((text) => {
          tasks.push({
            taskId: `${talentId}-${taskId}`,
            section: section,
            text: text,
            completed: false,
          })
          taskId++
        })
      }
    })

    if (missingTasksCreated) {
      console.log(`После добавления заглушек получено ${tasks.length} задач`)
    }

    return tasks
  }

  // Загрузка статуса выполнения задач (сначала из сервера, а затем из локального хранилища)
  const loadChecklistStatus = async (userId, talentId) => {
    try {
      // Сначала пробуем загрузить из API, если это возможно
      try {
        const result = await iprHook.getProgress(userId)
        if (!result.success) throw new Error('Failed to load progress from hook')
        const data = { success: result.success, progress: result.progress }

        if (data.success && data.progress && data.progress[talentId]) {
          console.log(`Загружен прогресс ИПР из базы данных для таланта ${talentId}`)
          return data.progress[talentId]
        }
      } catch (apiError) {
        console.warn(
          'Не удалось загрузить данные из API, используем локальное хранилище:',
          apiError,
        )
      }

      // Если не удалось загрузить из API, используем локальное хранилище
      const key = `ipr_checklist_${userId}_${talentId}`
      const savedData = localStorage.getItem(key)

      if (savedData) {
        console.log(`Загружен прогресс ИПР из localStorage для таланта ${talentId}`)
        return JSON.parse(savedData)
      }
    } catch (err) {
      console.error('Ошибка при загрузке статуса чек-листа:', err)
    }

    return []
  }

  // Сохранение статуса выполнения задач в базу данных и локальное хранилище
  const saveChecklistStatus = async (userId, talentId, tasks) => {
    try {
      // Подготавливаем данные для сохранения
      const dataToSave = tasks.map((task) => ({
        taskId: task.taskId,
        completed: task.completed,
      }))

      // Сохраняем в локальное хранилище (как резервный вариант)
      const key = `ipr_checklist_${userId}_${talentId}`
      localStorage.setItem(key, JSON.stringify(dataToSave))

      // Save in database using hook instead of API
      try {
        const result = await iprHook.saveProgress(userId, talentId, dataToSave)
        if (result.success) {
          console.log(`Прогресс ИПР для таланта ${talentId} успешно сохранен в базе данных`)
        } else {
          console.warn('Не удалось сохранить прогресс в базу данных:', result.error)
        }
      } catch (apiError) {
        console.warn('Ошибка при сохранении прогресса в базу данных:', apiError)
        console.log('Прогресс сохранен только локально')
      }
    } catch (err) {
      console.error('Ошибка при сохранении статуса чек-листа:', err)
    }
  }

  // Используем debounce для отложенного сохранения в базу данных
  const [savePending, setSavePending] = useState(false)
  const [saveTimeoutId, setSaveTimeoutId] = useState(null)

  // Оптимизированный обработчик изменения статуса задачи
  const handleTaskToggle = async (taskId) => {
    // Обновляем состояние в UI немедленно
    const updatedChecklist = checklist.map((task) =>
      task.taskId === taskId ? { ...task, completed: !task.completed } : task,
    )

    setChecklist(updatedChecklist)

    // Локально обновляем прогресс (быстрая операция)
    updateProgress(updatedChecklist)

    // Устанавливаем флаг, что изменения ожидают сохранения
    setSavePending(true)

    // Отменяем предыдущий отложенный вызов, если он есть
    if (saveTimeoutId) {
      clearTimeout(saveTimeoutId)
    }

    // Создаем новый отложенный вызов для сохранения состояния
    const timeoutId = setTimeout(() => {
      // Асинхронное сохранение в базу данных
      saveChecklistStatus(userId, talentId, updatedChecklist)
        .then(() => {
          console.log('Прогресс успешно сохранен')
          setSavePending(false)
        })
        .catch((error) => {
          console.error('Ошибка при сохранении статуса задачи:', error)
          setSavePending(false)
        })
    }, 1000) // Ждем 1 секунду бездействия пользователя

    setSaveTimeoutId(timeoutId)
  }

  // Функция для форматирования текста задачи (замена ** на <strong>)
  const formatTaskText = (text) => {
    if (!text) return ''

    // Заменяем пары ** на <strong> и </strong>
    const formattedText = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

    return formattedText
  }

  // Обновление прогресса выполнения - мемоизируем вычисления
  const updateProgress = React.useCallback((tasks) => {
    // Оптимизированный расчет
    let completedCount = 0
    const totalCount = tasks.length

    for (let i = 0; i < totalCount; i++) {
      if (tasks[i].completed) {
        completedCount++
      }
    }

    const progressValue = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

    // Обновляем только если изменилось
    setProgress((prevProgress) => {
      if (prevProgress !== progressValue) {
        return progressValue
      }
      return prevProgress
    })
  }, [])

  useEffect(() => {
    if (onProgressUpdate) {
      onProgressUpdate(talentId, progress)
    }
  }, [progress, talentId, onProgressUpdate])

  // Если данные загружаются
  if (loading) {
    return (
      <div className="ipr-checklist-loading">
        <div className="spinner"></div>
        <p>Загрузка плана развития...</p>
      </div>
    )
  }

  // Если произошла ошибка
  if (error) {
    return (
      <div className="ipr-checklist-error">
        <p>Не удалось загрузить план развития: {error}</p>
      </div>
    )
  }

  // Если чек-лист пуст
  if (checklist.length === 0) {
    return (
      <div className="ipr-checklist-empty">
        <p>План развития не содержит задач</p>
      </div>
    )
  }

  // Группируем задачи по разделам
  const groupedTasks = {}
  checklist.forEach((task) => {
    if (!groupedTasks[task.section]) {
      groupedTasks[task.section] = []
    }
    groupedTasks[task.section].push(task)
  })

  return (
    <div className={`ipr-checklist ${compact ? 'compact' : ''}`}>
      {/* Заголовок отображается только в полном режиме */}
      {!compact && (
        <div className="ipr-checklist-header">
          <h3>{talentName}</h3>
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <div className="progress-text">{progress}% выполнено</div>
          </div>
        </div>
      )}

      <div className="ipr-checklist-sections">
        {Object.keys(groupedTasks).map((section) => (
          <div key={section} className="ipr-checklist-section">
            <h4>{section}</h4>
            <ul className="task-list">
              {groupedTasks[section].map((task) => (
                <li key={task.taskId} className={`task-item ${task.completed ? 'completed' : ''}`}>
                  <label className="task-checkbox">
                    <input
                      type="checkbox"
                      checked={task.completed}
                      onChange={() => handleTaskToggle(task.taskId)}
                    />
                    <span className="checkmark"></span>
                    <span
                      className="task-text"
                      dangerouslySetInnerHTML={{ __html: formatTaskText(task.text) }}
                    ></span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

export default IPRChecklist
